import {
  EOrderDeliveryExpeditionType,
  EOrderDeliveryStatus,
  EOrderShippingOperator,
  IOrder,
  orderDeliveryStatusLabels,
} from '../../types'
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useAuthStore } from '../auth.context/auth.context'
import { useNotifications } from '../notification.context/notification.context'
import { dispatchAnalytics, gaEvents } from '@services/analytics'
import { useQuery } from 'react-query'
import { loadOrdersToAnswer } from '@services/hubii/hub-service'
import { LoadOrdersToAnswer } from '@services/hubii/hub-service/types'
import { Outlet } from 'react-router-dom'
import { socketClient } from '@services/hubii/notification-service'

import { useToast } from '@stores/toast.context'
import { useOrderListLayoutProvider } from '@stores/orders/orders-list-layout.context'

const OrdersProposalContext = createContext<OrdersProposalContextSpace.State>(
  {} as OrdersProposalContextSpace.State,
)

export const useOrdersProposals = () => useContext(OrdersProposalContext)

const OrdersProposalProvider = () => {
  const { selectedHub, user } = useAuthStore()
  const { audio, playNotificationSound, addPushNotification } = useNotifications()
  const { refetchAll } = useOrderListLayoutProvider()
  const { setToast } = useToast()

  const { data: orders, refetch: ordersRefetch } = useQuery({
    queryKey: ['orders', 'to_answer', selectedHub!.uuid],
    queryFn: () => loadOrdersToAnswer(selectedHub!.uuid),
    enabled: !!selectedHub,
  })

  const handleNewOrderReceived = useCallback(
    async (payload: { hubUuid: string; order: IOrder }) => {
      ordersRefetch()
      console.log('new order received:', payload.order)
      setToast('info', `Novo pedido #${payload.order.id} recebido!`)

      let soundPlayed = false
      let pushReceived = false

      if (playNotificationSound && typeof playNotificationSound === 'function') {
        try {
          await playNotificationSound()
          soundPlayed = true
        } catch (error) {
          console.error('Erro ao tocar som de notificação:', error)
        }
      }

      const findHub = user?.hubs?.find((hub) => hub.uuid === payload.hubUuid)

      if (addPushNotification && typeof addPushNotification === 'function') {
        try {
          await addPushNotification(
            `Novo Pedido Recebido ${findHub?.trading_name || findHub?.name || 'Hub'}`,
            {
              body: `Pedido #${payload.order.id} aguardando resposta`,
              icon: '/favicon.ico',
              badge: '/favicon.ico',
              data: {
                url: `/pedidos/${payload.order.uuid}`,
                orderId: payload.order.id,
                hubUuid: payload.hubUuid,
              },
            },
          )
          pushReceived = true
        } catch (error) {
          console.error('Erro ao adicionar notificação push:', error)
        }
      }

      dispatchAnalytics(
        gaEvents.order.receivedOrder({
          ...payload.order,
          notification: {
            pushReceived,
            soundPlayed,
          },
        }),
      )
    },
    [ordersRefetch, playNotificationSound, addPushNotification, setToast, user],
  )

  const handleAcceptedOrder = useCallback(
    (_: IOrder) => {
      ordersRefetch()
      audio?.pause()
    },
    [ordersRefetch],
  )

  const handleLostOrderAuction = useCallback(
    (payload: { hubUuid: string; order: IOrder }) => {
      dispatchAnalytics(gaEvents.order.lostOrder(payload.order))
      audio?.pause()
      ordersRefetch()
    },
    [ordersRefetch],
  )

  const handleCanceledOrder = useCallback(
    (payload: { hubUuid: string; order: IOrder }) => {
      ordersRefetch()
      dispatchAnalytics(gaEvents.order.canceledOrder(payload.order))
      audio?.pause()
    },
    [ordersRefetch],
  )

  const handleUpdateOrders = useCallback(
    (payload: {
      hubUuid: string
      old_status: EOrderDeliveryStatus
      new_status: EOrderDeliveryStatus
      order_id: string
      expedition_type: EOrderDeliveryExpeditionType
      shipping_operator: EOrderShippingOperator
      has_invoice: boolean
      is_packed: boolean
    }) => {
      setToast(
        'info',
        `Pedido ${payload.order_id} atualizado! Novo status: ${orderDeliveryStatusLabels(
          payload.new_status,
          payload.expedition_type,
          payload.shipping_operator,
          payload.has_invoice,
          payload.is_packed,
        )}`,
      )

      refetchAll()
    },
    [refetchAll],
  )

  useEffect(() => {
    socketClient.on('remove-notification', handleLostOrderAuction)
    socketClient.on('canceled', handleCanceledOrder)
    socketClient.on('order-accepted', handleAcceptedOrder)
    socketClient.on('new-order', handleNewOrderReceived)
    socketClient.on('order-change-status', handleUpdateOrders)

    return () => {
      socketClient.off('new-order', handleNewOrderReceived)
      socketClient.off('order-accepted', handleAcceptedOrder)
      socketClient.off('remove-notification', handleLostOrderAuction)
      socketClient.off('canceled', handleCanceledOrder)
      socketClient.off('order-change-status', handleUpdateOrders)
    }
  }, [
    handleNewOrderReceived,
    handleAcceptedOrder,
    handleLostOrderAuction,
    handleCanceledOrder,
    handleUpdateOrders,
  ])

  const contextValue = useMemo(() => {
    return {
      ordersProposal: orders ?? null,
      ordersProposalLoad: () => ordersRefetch(),
    }
  }, [orders, ordersRefetch])

  return (
    <OrdersProposalContext.Provider value={contextValue}>
      <Outlet />
    </OrdersProposalContext.Provider>
  )
}

export default OrdersProposalProvider

namespace OrdersProposalContextSpace {
  export type State = {
    ordersProposal: LoadOrdersToAnswer.Response | null
    ordersProposalLoad: () => void
  }
}
